import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, Play, RotateCcw, ScrollText, Square, Trash2 } from 'lucide-react';
import { Job, JobAction, JobType, ParamValue, Schedule } from '../../services/JobMonitorService';
import { ConfirmDialog } from '../../components/library/modals/ConfirmDialog';
import { ParamsEditor } from './ParamsEditor';
import { ScheduleEditor } from './ScheduleEditor';
import { formatExitResult, formatTimestamp, statusBadgeClass, statusLabel, statusTooltip } from './formatters';
import styles from '../JobsPage.module.css';

interface JobRowProps {
  job: Job;
  jobType: JobType | null;
  busyAction: JobAction | null;
  onAction: (jobId: string, action: JobAction) => Promise<void>;
  onSaveSchedule: (jobId: string, schedule: Schedule, enabled: boolean) => Promise<void>;
  onClearSchedule: (jobId: string) => Promise<void>;
  onSaveParams: (jobId: string, params: Record<string, ParamValue>) => Promise<void>;
  onDelete: (job: Job) => Promise<void>;
  onViewLogs: (job: Job) => void;
  onViewErrors: (job: Job) => void;
}

const describeParams = (job: Job): string => {
  const entries = Object.entries(job.params);
  if (entries.length === 0) return '—';
  return entries.map(([name, value]) => `${name}=${Array.isArray(value) ? value.join('|') : value}`).join(', ');
};

// A job with a run in flight cannot be started again; only a run that has not
// already been asked to stop can be stopped.
const ACTIVE_STATES = new Set(['running', 'starting', 'stopping']);
const STOPPABLE_STATES = new Set(['running', 'starting']);

export const JobRow: React.FC<JobRowProps> = ({
  job,
  jobType,
  busyAction,
  onAction,
  onSaveSchedule,
  onClearSchedule,
  onSaveParams,
  onDelete,
  onViewLogs,
  onViewErrors,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingAction, setPendingAction] = useState<JobAction | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const isActive = ACTIVE_STATES.has(job.status);
  const canStop = STOPPABLE_STATES.has(job.status);
  const busy = busyAction !== null || deleting;

  const trigger = async (action: JobAction) => {
    if (canStop && (action === 'stop' || action === 'restart')) {
      setPendingAction(action);
      return;
    }
    await onAction(job.job_id, action);
  };

  const runPendingAction = async () => {
    const action = pendingAction;
    setPendingAction(null);
    if (action) await onAction(job.job_id, action);
  };

  const actionIcon = (action: JobAction, fallback: React.ReactNode) =>
    busyAction === action ? <Loader2 size={14} className={styles.spin} /> : fallback;

  // Deleting removes the units, the registry entry, and all stored history, so
  // the job id has to be typed out rather than confirmed with one click.
  const remove = async () => {
    setConfirmingDelete(false);
    setDeleting(true);
    try {
      await onDelete(job);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <button
          type="button"
          className={styles.expandBtn}
          onClick={() => setExpanded((value) => !value)}
          aria-label={expanded ? 'Collapse job details' : 'Expand job details'}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <div className={styles.nameBlock}>
          <h3 className={styles.jobName}>{job.display_name}</h3>
          <p className={styles.jobDescription}>{job.description}</p>
        </div>

        <span className={statusBadgeClass(job.status)} title={statusTooltip(job)}>
          {statusLabel(job.status)}
        </span>

        <div className={styles.actions}>
          <button type="button" className={styles.primaryBtn} disabled={busy || isActive} onClick={() => trigger('start')}>
            {actionIcon('start', <Play size={14} />)} Start
          </button>
          <button type="button" className={styles.dangerBtn} disabled={busy || !canStop} onClick={() => trigger('stop')}>
            {actionIcon('stop', <Square size={14} />)} Stop
          </button>
          <button
            type="button"
            className={styles.secondaryBtn}
            disabled={busy || job.status === 'stopping'}
            onClick={() => trigger('restart')}
          >
            {actionIcon('restart', <RotateCcw size={14} />)} Restart
          </button>
          <button type="button" className={styles.secondaryBtn} disabled={busy} onClick={() => onViewLogs(job)}>
            <ScrollText size={14} /> View Logs
          </button>
          <button type="button" className={styles.secondaryBtn} disabled={busy} onClick={() => onViewErrors(job)}>
            <AlertTriangle size={14} /> View Errors
          </button>
          <button type="button" className={styles.dangerBtn} disabled={busy} onClick={() => setConfirmingDelete(true)}>
            {deleting ? <Loader2 size={14} className={styles.spin} /> : <Trash2 size={14} />} Delete
          </button>
        </div>
      </div>

      <div className={styles.metaRow}>
        <div>
          <span className={styles.metaLabel}>PID</span>
          <span className={styles.metaValue}>{job.pid ?? '—'}</span>
        </div>
        <div>
          <span className={styles.metaLabel}>Schedule</span>
          <span className={styles.metaValue}>{job.schedule_summary}</span>
        </div>
        <div>
          <span className={styles.metaLabel}>Last execution</span>
          <span className={styles.metaValue}>{formatTimestamp(job.last_start_at)}</span>
        </div>
        <div>
          <span className={styles.metaLabel}>Last result</span>
          <span className={styles.metaValue}>{formatExitResult(job)}</span>
        </div>
        <div>
          <span className={styles.metaLabel}>Next run</span>
          <span className={styles.metaValue}>{formatTimestamp(job.next_run_at)}</span>
        </div>
      </div>

      {expanded && (
        <div className={styles.details}>
          <div>
            <h4 className={styles.sectionTitle}>Job details</h4>
            <div className={styles.detailList}>
              <span className={styles.detailKey}>Job id</span>
              <span className={styles.detailValue}>{job.job_id}</span>
              <span className={styles.detailKey}>Job type</span>
              <span className={styles.detailValue}>{job.job_type}</span>
              <span className={styles.detailKey}>Parameters</span>
              <span className={styles.detailValue}>{describeParams(job)}</span>
              <span className={styles.detailKey}>Run state</span>
              <span className={styles.detailValue}>{job.active_state} / {job.sub_state}</span>
              <span className={styles.detailKey}>Last start</span>
              <span className={styles.detailValue}>{formatTimestamp(job.last_start_at)}</span>
              <span className={styles.detailKey}>Last stop</span>
              <span className={styles.detailValue}>{formatTimestamp(job.last_stop_at)}</span>
              <span className={styles.detailKey}>Last exit code</span>
              <span className={styles.detailValue}>{job.last_exit_code ?? '—'}</span>
              <span className={styles.detailKey}>Last error</span>
              <span className={styles.detailValue}>{job.last_error ?? '—'}</span>
              <span className={styles.detailKey}>Execution id</span>
              <span className={styles.detailValue}>{job.execution_id ?? '—'}</span>
              <span className={styles.detailKey}>Timer active</span>
              <span className={styles.detailValue}>{job.timer_active ? 'yes' : 'no'}</span>
            </div>
          </div>

          <ParamsEditor
            job={job}
            jobType={jobType}
            onSave={(params) => onSaveParams(job.job_id, params)}
          />

          <ScheduleEditor
            job={job}
            onSave={(schedule, enabled) => onSaveSchedule(job.job_id, schedule, enabled)}
            onClear={() => onClearSchedule(job.job_id)}
          />
        </div>
      )}

      {pendingAction && (
        <ConfirmDialog
          title={`${pendingAction === 'stop' ? 'Stop' : 'Restart'} job`}
          message={
            pendingAction === 'stop'
              ? `This kills the current run of "${job.display_name}". The schedule stays — the job will run again at its next scheduled time. Use Clear schedule to stop future runs.`
              : `"${job.display_name}" is currently running. Restarting kills the current run and starts a new one. Restart it now?`
          }
          confirmLabel={pendingAction === 'stop' ? 'Stop job' : 'Restart job'}
          variant="danger"
          onConfirm={runPendingAction}
          onCancel={() => setPendingAction(null)}
        />
      )}

      {confirmingDelete && (
        <ConfirmDialog
          title={`Delete "${job.display_name}"`}
          message="This removes its systemd units, its registry entry, and all of its stored history. This cannot be undone."
          confirmLabel="Delete job"
          variant="danger"
          confirmPhrase={job.job_id}
          confirmPhraseLabel="Type the job id to confirm"
          onConfirm={remove}
          onCancel={() => setConfirmingDelete(false)}
        />
      )}
    </div>
  );
};
